import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import TextButton from '@/components/@IntranetLibrary/Button/TextButton';
import InfoSection from '@/components/Approval/@components/InfoSection';
import ContractStatusBadge from '@/components/Approval/LinkBlock/ContractStatusBadge';
import NewWindowIcon from '@/public/icons/approval/new-window.svg';
import TrashIcon from '@/public/icons/approval/trash.svg';
import type { ElawContract } from '@/types/approval';
import ContractSummaryTable from './ContractSummaryTable';
import { formatContractText, toContractHref } from './format';
import {
  ContractActions,
  ContractBlock,
  ContractBlockHeader,
  ContractBlockTitle,
  ContractInfoRoot,
} from './styles';

export { default as ContractSummaryTable } from './ContractSummaryTable';
export * from './format';
export * from './snapshot';

type ContractInfoListProps = {
  /** contractIds 순서 그대로 */
  contracts: ElawContract[];
  title?: ReactNode;
  /** 작성 화면 전용 — 모든 계약에 삭제 버튼 */
  onRemove?: (contractId: number) => void;
  /** 계약 묶음 맨 아래(작성 화면 AI 초안 상태 등) */
  renderFooter?: (contractId: number) => ReactNode;
  className?: string;
};

/**
 * 계약 품의(117·118) 계약 정보 — 작성·조회 공용. 계약마다 같은 묶음(계약명·상태·버튼 + 상세 표).
 * 상세보기는 법틀 계약 화면(accessLink)이라 링크가 있을 때만
 */
const ContractInfoList = ({
  contracts,
  title,
  onRemove,
  renderFooter,
  className,
}: ContractInfoListProps) => {
  const { t } = useTranslation();
  if (!contracts.length) return null;

  return (
    <InfoSection title={title} className={className}>
      <ContractInfoRoot>
        {contracts.map((contract) => {
          const href = toContractHref(contract.accessLink);
          const removable = Boolean(onRemove);
          return (
            <ContractBlock key={contract.contractId}>
              <ContractBlockHeader>
                <ContractBlockTitle>
                  <span>{formatContractText(contract.contractName)}</span>
                  {contract.status && (
                    <ContractStatusBadge
                      status={contract.status}
                      fallbackLabel={contract.statusName ?? undefined}
                    />
                  )}
                </ContractBlockTitle>
                {(removable || href) && (
                  <ContractActions>
                    {href && (
                      <BoxButton
                        variant="secondary"
                        size="small"
                        endIcon={<NewWindowIcon />}
                        onClick={() =>
                          window.open(href, '_blank', 'noopener,noreferrer')
                        }
                      >
                        {t('상세보기')}
                      </BoxButton>
                    )}
                    {removable && (
                      <TextButton
                        type="button"
                        variant="caution"
                        size="small"
                        startIcon={<TrashIcon />}
                        onClick={() => onRemove?.(contract.contractId)}
                        aria-label={t('계약 삭제')}
                      >
                        {t('삭제')}
                      </TextButton>
                    )}
                  </ContractActions>
                )}
              </ContractBlockHeader>
              <ContractSummaryTable contract={contract} />
              {renderFooter?.(contract.contractId)}
            </ContractBlock>
          );
        })}
      </ContractInfoRoot>
    </InfoSection>
  );
};

export default ContractInfoList;
